// src/pages/blog.jsx – Vollständig optimiert für SEO & mobile Darstellung
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import Container from '../components/container';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt,
  faClock,
  faSearch,
  faFilter,
  faTimes,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import BlogService from '../services/blogService';

// Sidebar Filter Komponente
const CategoryFilterList = ({ categories, activeFilter, handleCategoryFilter, t }) => (
  <div className="mb-4">
    <h3 className="font-semibold text-white mb-2 font-heading">
      {t('categories', 'Kategorien')}
    </h3>
    <div className="flex flex-col gap-2">
      <button
        onClick={() => handleCategoryFilter('all')}
        className={`text-left px-3 py-2 rounded text-sm transition-colors ${
          activeFilter === 'all'
            ? 'bg-primary text-white'
            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
        }`}
      >
        {t('all-posts', 'Alle Beiträge')}
      </button>
      <div className="max-h-60 overflow-y-auto pr-1 space-y-1 scrollbar-thin scrollbar-thumb-gray-600">
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => handleCategoryFilter(category)}
            className={`text-left px-3 py-2 rounded text-sm transition-colors ${
              activeFilter === category
                ? 'bg-primary text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {category}
          </button>
        ))}
      </div>
    </div>
  </div>
);

const Blog = () => {
  const { t, i18n } = useTranslation('blog');
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const getCurrentLanguage = () => {
    const fullLang = i18n.language || localStorage.getItem('i18nextLng') || 'de_DE';
    const simpleLang = fullLang.split('_')[0];
    return { fullLang, simpleLang };
  };

  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(i18n.language || 'de-DE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const handleCategoryFilter = (category) => {
    setActiveFilter(category);
    applyFilters(searchTerm, category, posts);
    const params = new URLSearchParams(searchParams);
    category === 'all' ? params.delete('category') : params.set('category', category);
    setSearchParams(params);
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    applyFilters(term, activeFilter, posts);
    const params = new URLSearchParams(searchParams);
    term === '' ? params.delete('search') : params.set('search', term);
    setSearchParams(params);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setActiveFilter('all');
    setFilteredPosts([...posts]);
    navigate('/blog');
  };

  const applyFilters = (term, category, postsToFilter) => {
    const { fullLang, simpleLang } = getCurrentLanguage();
    let filtered = [...postsToFilter];

    if (category !== 'all') {
      filtered = filtered.filter(post => post.categories.includes(category));
    }

    if (term) {
      filtered = filtered.filter(post => {
        const trans = post.translations || {};
        const langData = trans[fullLang] || trans[simpleLang] || {};
        const title = langData.title || post.title || '';
        const excerpt = langData.excerpt || post.excerpt || '';
        const content = langData.searchableContent || (typeof langData.content === 'string' ? langData.content : '') || '';
        return [title, excerpt, content].some(text => text.toLowerCase().includes(term));
      });
    }

    setFilteredPosts(filtered);
  };

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const allPosts = await BlogService.getAllPosts();
        setPosts(allPosts);
        const allCategories = allPosts.flatMap(post => post.categories);
        const uniqueCategories = [...new Set(allCategories)].sort();
        setCategories(uniqueCategories);

        const categoryParam = searchParams.get('category') || 'all';
        const searchParam = searchParams.get('search') || '';
        setActiveFilter(categoryParam);
        setSearchTerm(searchParam);
        applyFilters(searchParam, categoryParam, allPosts);
        setLoading(false);
      } catch (error) {
        console.error('Fehler beim Laden der Beiträge:', error);
        setLoading(false);
      }
    };

    fetchPosts();
  }, [searchParams, i18n.language]);

  const getTranslatedContent = (post) => {
    const { fullLang, simpleLang } = getCurrentLanguage();
    const trans = post.translations || {};
    return trans[fullLang] || trans[simpleLang] || post;
  };

  if (loading) {
    return (
      <Container>
        <div className="flex justify-center items-center h-64">
          <div className="text-primary">{t('loading', 'Lade Beiträge...')}</div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Helmet>
        <title>Blog – PathConnect</title>
        <meta name="description" content="Neuigkeiten, Anleitungen und Beiträge zu Open Source IT-Lösungen von PathConnect." />
        <link rel="canonical" href="https://pathconnect.eu/blog" />
        <meta property="og:title" content="Blog – PathConnect" />
        <meta property="og:description" content="Neuigkeiten, Anleitungen und Beiträge zu Open Source IT-Lösungen von PathConnect." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pathconnect.eu/blog" />
        <meta property="og:image" content="https://pathconnect.eu/assets/images/social-preview.png" />
      </Helmet>

      <div className="max-w-6xl mx-auto py-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Filter Sidebar */}
          <aside className="w-full md:w-1/4">
            <div className="bg-gray-800 rounded-lg shadow-md p-4 sticky top-24">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white font-heading">{t('filters', 'Filter')}</h2>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="md:hidden text-gray-300"
                  aria-label={showFilters ? 'Filter ausblenden' : 'Filter anzeigen'}
                >
                  <FontAwesomeIcon icon={showFilters ? faTimes : faFilter} />
                </button>
              </div>

              <div className={`${showFilters ? 'block' : 'hidden'} md:block`}>
                <div className="mb-6 relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearch}
                    placeholder={t('search-blog', 'Blog durchsuchen...')}
                    className="w-full px-4 py-2 pl-10 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-primary focus:outline-none"
                  />
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                </div>

                <CategoryFilterList
                  categories={categories}
                  activeFilter={activeFilter}
                  handleCategoryFilter={handleCategoryFilter}
                  t={t}
                />

                {(activeFilter !== 'all' || searchTerm) && (
                  <button
                    onClick={resetFilters}
                    className="w-full px-4 py-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors"
                  >
                    {t('reset-filters', 'Filter zurücksetzen')}
                  </button>
                )}
              </div>
            </div>
          </aside>

          {/* Blog Grid */}
          <section className="w-full md:w-3/4">
            {filteredPosts.length === 0 ? (
              <div className="text-center py-12 bg-gray-800 rounded-lg shadow-md">
                <p className="text-gray-300 text-lg">
                  {t('no-posts-found', 'Keine Beiträge gefunden.')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {filteredPosts.map((post) => {
                  const { title, excerpt, formattedDate } = getTranslatedContent(post);
                  return (
                    <article
                      key={post.id}
                      className="bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden"
                    >
                      {post.thumbnailUrl && (
                        <div className="h-48 overflow-hidden">
                          <img
                            src={post.thumbnailUrl}
                            alt={title}
                            className="w-full h-full object-cover transform transition-transform duration-500 hover:scale-105"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/600x300?text=PathConnect';
                            }}
                          />
                        </div>
                      )}

                      <div className="p-6">
                        <div className="flex flex-wrap text-sm text-gray-400 mb-2">
                          <div className="flex items-center mr-4 mb-2">
                            <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                            {formattedDate || formatDate(post.date)}
                          </div>
                          {post.readTime && (
                            <div className="flex items-center mb-2">
                              <FontAwesomeIcon icon={faClock} className="mr-1" />
                              {post.readTime} min {t('read-time', 'Lesezeit')}
                            </div>
                          )}
                        </div>

                        <h2 className="text-xl font-bold text-white mb-3 hover:text-primary transition-colors font-heading">
                          <Link to={`/blog/${post.slug}`}>{title}</Link>
                        </h2>

                        <div className="flex flex-wrap mb-4 gap-2">
                          {post.categories.map((cat) => (
                            <span
                              key={cat}
                              onClick={(e) => {
                                e.preventDefault();
                                handleCategoryFilter(cat);
                              }}
                              className="text-xs px-3 py-1 bg-gray-700 text-primary rounded-full hover:bg-gray-600 transition-colors cursor-pointer"
                            >
                              {cat}
                            </span>
                          ))}
                        </div>

                        {excerpt && (
                          <p className="text-gray-300 mb-4 line-clamp-3">{excerpt}</p>
                        )}

                        <Link
                          to={`/blog/${post.slug}`}
                          className="inline-flex items-center text-primary font-medium hover:text-button-dark"
                        >
                          {t('read-more', 'Weiterlesen')}
                          <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                        </Link>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>
    </Container>
  );
};

export default Blog;
